// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Star, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";
import { getSessionUser } from '@/lib/authSession';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

const emptyForm = {
  id: null,
  name: "",
  html_content: "",
  image_url: "",
  link_url: "",
  is_active: true,
  is_default: false,
};

function BrandingSection({ type, table, scopeOrgId, createOrgId }: any) {
  const queryClient = useQueryClient();
  const isHeader = type === "header";
  const [form, setForm] = useState<any>(emptyForm);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (file: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `email-branding/${createOrgId || "shared"}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: upErr } = await (supabase as any).storage
        .from("org-logos")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data } = (supabase as any).storage.from("org-logos").getPublicUrl(path);
      setForm((f: any) => ({ ...f, image_url: data.publicUrl }));
      toast.success("Image uploaded");
    } catch (err: any) {
      toast.error("Upload failed: " + (err?.message || "unknown"));
    } finally {
      setUploading(false);
    }
  };

  const queryKey = [`org-${type}s`, scopeOrgId];
  const { data: items = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      let q = (supabase as any).from(table).select("*").order("sort_order", { ascending: true });
      if (scopeOrgId) q = q.eq("organization_id", scopeOrgId);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });

  const startCreate = () => {
    setForm({ ...emptyForm });
    setEditing(true);
  };

  const startEdit = (item: any) => {
    setForm({
      id: item.id,
      name: item.name || "",
      html_content: item.html_content || "",
      image_url: item.image_url || "",
      link_url: item.link_url || "",
      is_active: item.is_active ?? true,
      is_default: item.is_default ?? false,
    });
    setEditing(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const { data: { user } } = await getSessionUser();
      const payload: any = {
        name: form.name.trim(),
        html_content: form.html_content || null,
        is_active: form.is_active,
        is_default: form.is_default,
      };
      if (isHeader) {
        payload.image_url = form.image_url || null;
        payload.link_url = form.link_url || null;
      }
      if (form.id) {
        const { error } = await (supabase as any).from(table).update(payload).eq("id", form.id);
        if (error) throw error;
        toast.success("Saved");
      } else {
        const { error } = await (supabase as any).from(table).insert({
          ...payload,
          organization_id: createOrgId,
          created_by: user?.id || null,
        });
        if (error) throw error;
        toast.success("Created");
      }
      setEditing(false);
      setForm({ ...emptyForm });
      queryClient.invalidateQueries({ queryKey });
    } catch (err: any) {
      toast.error("Save failed: " + (err?.message || "unknown"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this item?")) return;
    const { error } = await (supabase as any).from(table).delete().eq("id", id);
    if (error) {
      toast.error("Delete failed: " + error.message);
      return;
    }
    toast.success("Deleted");
    queryClient.invalidateQueries({ queryKey });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{isHeader ? "Email Headers" : "Email Footers"}</h3>
        {!editing && (
          <Button size="sm" onClick={startCreate}>
            <Plus className="h-4 w-4 mr-2" /> Add {isHeader ? "Header" : "Footer"}
          </Button>
        )}
      </div>

      {editing && (
        <Card className="border-primary">
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={isHeader ? "e.g. Branded Logo Header" : "e.g. Standard Footer"}
              />
            </div>

            {isHeader && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Logo banner image</Label>
                  <div className="flex items-center gap-3">
                    {form.image_url ? (
                      <img
                        src={form.image_url}
                        alt="Header logo"
                        className="h-12 max-w-[160px] object-contain rounded border border-border bg-white p-1"
                      />
                    ) : (
                      <div className="h-12 w-[160px] rounded border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">
                        No image
                      </div>
                    )}
                    <div className="flex flex-col gap-1">
                      <input
                        id="header-image-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(file);
                          e.target.value = "";
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploading}
                        onClick={() => document.getElementById("header-image-upload")?.click()}
                      >
                        {uploading ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                        ) : (
                          <><Upload className="h-4 w-4 mr-2" /> {form.image_url ? "Replace image" : "Upload image"}</>
                        )}
                      </Button>
                      {form.image_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => setForm({ ...form, image_url: "" })}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Link URL</Label>
                  <Input
                    value={form.link_url}
                    onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                    placeholder="https://yourorg.com"
                  />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>{isHeader ? "HTML content (optional, used if no image)" : "HTML content"}</Label>
              <div className="bg-white text-black rounded-md border border-border overflow-hidden">
                <ReactQuill
                  theme="snow"
                  value={form.html_content}
                  onChange={(v) => setForm({ ...form, html_content: v })}
                  modules={QUILL_MODULES}
                  style={{ minHeight: 180 }}
                />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} />
                <Label>Default</Label>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setEditing(false); setForm({ ...emptyForm }); }}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center py-8 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
        </div>
      ) : items.length === 0 && !editing ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No {type}s created yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((item: any) => (
            <Card key={item.id} className={item.is_active ? "" : "opacity-60"}>
              <CardContent className="py-3 flex items-center gap-3">
                {isHeader && item.image_url && (
                  <img src={item.image_url} alt={item.name} className="h-10 max-w-[120px] object-contain rounded" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{item.name}</span>
                    {item.is_default && (
                      <Badge variant="secondary" className="gap-1">
                        <Star className="h-3 w-3" /> Default
                      </Badge>
                    )}
                    {!item.is_active && <Badge variant="outline">Inactive</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => startEdit(item)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

const OrgEmailBrandingPage: React.FC = () => {
  const { scopeOrgId, createOrgId, isSuperAdmin, ready } = useAdminOrgScope();

  useEffect(() => {
    document.title = "Email Branding | RecruitLook Events";
  }, []);

  if (!ready) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Branding (Headers &amp; Footers)</h1>
        <p className="text-muted-foreground">
          Set your organization's branded email header and footer. These replace the generic
          branding on emails sent for your events.
        </p>
        {isSuperAdmin && (
          <p className="text-sm text-muted-foreground mt-1">
            New items are created for the host organization (platform admin).
          </p>
        )}
      </div>

      <Tabs defaultValue="headers" className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="headers">Headers</TabsTrigger>
          <TabsTrigger value="footers">Footers</TabsTrigger>
        </TabsList>
        <TabsContent value="headers">
          <BrandingSection
            type="header"
            table="message_email_headers"
            scopeOrgId={scopeOrgId}
            createOrgId={createOrgId}
          />
        </TabsContent>
        <TabsContent value="footers">
          <BrandingSection
            type="footer"
            table="message_email_footers"
            scopeOrgId={scopeOrgId}
            createOrgId={createOrgId}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OrgEmailBrandingPage;
